import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import _ from "lodash";
import React from "react";
import { useFragment } from "react-relay";

import { Text } from "@whoosh/design-system/src/components/primitive";

import { useFormatClubCurrency } from "../../../../../../ClubCurrency";
import { mergeEligibleWindowCharges } from "../../../../../utils/mergeEligibleWindowCharges";
import {
  isStaffAdminAtom,
  userTypeAtom,
} from "../../../atoms/eventSignUpProcess";
import { useGetAllPrimaryChargeSelectionOptions_event$key } from "./__generated__/useGetAllPrimaryChargeSelectionOptions_event.graphql";

type useGetAllPrimaryChargeSelectionOptionsProps = {
  eventRef: useGetAllPrimaryChargeSelectionOptions_event$key;
};

export const useGetAllPrimaryChargeSelectionOptions = ({
  eventRef,
}: useGetAllPrimaryChargeSelectionOptionsProps) => {
  const formatPrice = useFormatClubCurrency();
  const event = useFragment(
    useGetAllPrimaryChargeSelectionOptionsFragmentString,
    eventRef
  );
  const isMemberUsingForm = useAtomValue(userTypeAtom) === "MEMBER";
  const isStaffAdmin = useAtomValue(isStaffAdminAtom);

  // Members see charges from the windows they're eligible for (the backend
  // strips HIDDEN charges); staff see charges across all windows, gated by
  // staffControl + their admin role.
  const charges = mergeEligibleWindowCharges(
    isMemberUsingForm
      ? event.eligibleWindowsForCharges
      : event.signUpWindows?.edges?.map((e) => e?.node)
  );

  const primaryChargeOptions = charges.flatMap(
    ({ node, memberControl, staffControl }) => {
      if (isMemberUsingForm) {
        // Members can only pick SELECTABLE charges.
        if (memberControl !== "SELECTABLE") {
          return [];
        }
      } else {
        // Staff can pick any charge except ADMIN_ONLY, which requires an admin.
        const staffCanSelect = staffControl !== "ADMIN_ONLY" || isStaffAdmin;
        if (!staffCanSelect) {
          return [];
        }
      }

      const price = node.chargeRuleSet.chargeRules?.edges?.[0]?.node?.price;
      const currentChargeRuleRawPrice = _.isNumber(price) ? price : null;
      const currentChargeRuleFormattedPrice =
        currentChargeRuleRawPrice !== null
          ? formatPrice(currentChargeRuleRawPrice)
          : null;

      if (
        node.id &&
        node.chargeRuleSet.displayName &&
        currentChargeRuleFormattedPrice
      ) {
        const ChargeOption = {
          value: node.id,
          label: node.chargeRuleSet.displayName,
          rightElement: (
            <Text
              variant="mainBaseMedium"
              color="text-secondary"
            >{`${currentChargeRuleFormattedPrice}`}</Text>
          ),
        };
        return [ChargeOption];
      }

      return [];
    }
  );

  return primaryChargeOptions;
};

const useGetAllPrimaryChargeSelectionOptionsFragmentString = graphql`
  fragment useGetAllPrimaryChargeSelectionOptions_event on Event {
    signUpWindows {
      edges {
        node {
          charges {
            edges {
              memberControl
              staffControl
              node {
                id
                chargeRuleSet {
                  id
                  displayName
                  displayDescription
                  chargeRules {
                    edges {
                      node {
                        price
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    # Member users see only the windows they are eligible for (caller-scoped).
    # Aliased so it never field-merges with other clientEligibleSignUpWindows
    # selections (e.g. the pricing section) that pass a clientId argument.
    eligibleWindowsForCharges: clientEligibleSignUpWindows {
      charges {
        edges {
          memberControl
          staffControl
          node {
            id
            chargeRuleSet {
              id
              displayName
              displayDescription
              chargeRules {
                edges {
                  node {
                    price
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
